/**
 * Add-on trials on the web (docs/FEATURE-TRIALS.md; rules and words in core featuretrials.ts).
 * - FeatureTrialCards: on Today, for owners and admins only. The thank-you card (days 1 to 11), the recap with "Keep" (the last
 *   2 days), the quiet "off" notes. Staff get nothing: the server returns an empty list to them and this never renders for them.
 * - TrialChip: a quiet "N days left" on the feature's own screen, owners and admins only.
 * "Keep" is the only tap that costs money. Its label carries the price, the line above it says what it does to the bill, and
 * "No thanks" is as big as it. Doing nothing ends the trial with no charge (FTC / ROSCA: no charge without an explicit tap).
 */
import { viaApp } from "../lib/storeApp.ts";
import { useEffect, useState } from "react";
import { isHomeOrg } from "../api.ts";
import type { FeatureTrialView } from "@logwave/core";
import { useLoad } from "../lib/useLoad.ts";
import { useApi, useSession } from "../session.tsx";
import { isDemoSample } from "../lib/demoAsk.ts";
import { Icon } from "./Icons.tsx";
import { Button, Card, ErrorBox, LinkButton, Pill } from "./ui.tsx";

const CHANGED = "cc:features-changed";
function useTrials() {
  const api = useApi();
  const s = useSession();
  // The sample café has no plan to add to: it never shows an offer.
  const on = s.can("admin") && !isHomeOrg(s.org) && !isDemoSample(s.org);
  const load = useLoad(() => (on ? api.features(s.org.id) : Promise.resolve({ items: [], people: 0 })), [api, s.org.id, on]);
  // The card and the chip are separate parts of the page: when one is tapped, the other hears it.
  useEffect(() => {
    const again = () => void load.reload();
    window.addEventListener(CHANGED, again);
    return () => window.removeEventListener(CHANGED, again);
  }, [load.reload]); // eslint-disable-line react-hooks/exhaustive-deps
  return { items: load.data?.items ?? [], load, orgId: s.org.id };
}

export function FeatureTrialCards({ compact }: { compact?: boolean }) {
  return viaApp() ? null : <FeatureTrialCardsHere compact={compact} />;
}

function FeatureTrialCardsHere({ compact }: { compact?: boolean }) {
  const { items, load, orgId } = useTrials();
  if (!items.length) return null;
  return (
    <div className="stack-sm" data-testid="feature-trials">
      {items.map((v) => <TrialCard key={v.featureId} v={v} orgId={orgId} compact={compact} onChange={() => void load.reload()} />)}
    </div>
  );
}

function TrialCard({ v, orgId, compact, onChange }: { v: FeatureTrialView; orgId: string; compact?: boolean; onChange: () => void }) {
  const api = useApi();
  const [busy, setBusy] = useState<"keep" | "no" | null>(null);
  const [error, setError] = useState<string>();
  const [kept, setKept] = useState(false);
  const c = v.card;
  const run = async (kind: "keep" | "no") => {
    setBusy(kind); setError(undefined);
    try {
      if (kind === "keep") { await api.keepFeature(orgId, v.featureId); setKept(true); } else await api.declineFeature(orgId, v.featureId);
      onChange();
      window.dispatchEvent(new Event(CHANGED));
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setBusy(null); }
  };
  if (kept) {
    return (
      <div data-testid="trial-kept"><Card edge="go" className="stack-sm">
        <p className="row"><Icon name="check" size={20} /><strong>{v.label} stays on.</strong></p>
        <p className="small">The part of this month that is left is on your next invoice. You can turn it off any time in Settings.</p>
      </Card></div>
    );
  }
  const body = (
    <div className="stack-sm">
      <h3 className="row"><Icon name={c.kind === "recap" ? "clock" : c.kind === "gift" ? "spark" : "info"} size={20} />{c.title}</h3>
      {c.lines.map((l, i) => <p key={i} className={i === 0 && c.kind === "gift" ? "" : "small"}>{i === 0 && c.kind === "gift" ? <strong>{l}</strong> : l}</p>)}
      {c.keep && (
        <>
          <p className="small" data-testid="keep-detail">{c.keep.detail}</p>
          <ErrorBox error={error} />
          <div className="form-actions">
            <Button big icon="check" busy={busy === "keep"} disabled={!!busy} onClick={() => void run("keep")}>{c.keep.label}</Button>
            <Button variant="secondary" disabled={!!busy} busy={busy === "no"} onClick={() => void run("no")}>{c.noThanks ?? "No thanks"}</Button>
          </div>
        </>
      )}
      {!c.keep && c.tryPath && (
        <>
          <ErrorBox error={error} />
          <div className="form-actions">
            <LinkButton to={c.tryPath} icon="chevron" iconEnd>{`Try ${v.label}`}</LinkButton>
            <Button variant="ghost" disabled={!!busy} busy={busy === "no"} onClick={() => void run("no")}>{c.noThanks ?? "No thanks"}</Button>
          </div>
        </>
      )}
    </div>
  );
  const edge = c.kind === "recap" ? "info" : c.kind === "gift" ? "go" : undefined;
  // During service the card is one line until opened: nothing here is more urgent than the food (moment context).
  if (compact && (c.kind === "gift" || c.kind === "recap")) {
    return (
      <div data-testid={`trial-${c.kind}`}><details className="card card-flat">
        <summary className="disclose">{c.kind === "gift" ? `${v.label} is on for you, free` : c.title}{v.chip ? ` · ${v.chip}` : ""}</summary>
        <div style={{ marginTop: 12 }}>{body}</div>
      </details></div>
    );
  }
  return <div data-testid={`trial-${c.kind}`}><Card edge={edge} flat={!edge}>{body}</Card></div>;
}

/** The quiet chip on the feature's own screen. Owners and admins only; nothing for staff, nothing once the trial is over. */
export function TrialChip({ featureId }: { featureId: string }) {
  const { items } = useTrials();
  const v = items.find((i) => i.featureId === featureId && i.state === "trial");
  if (!v?.chip) return null;
  return <Pill tone="info" icon="clock" title={`Free trial of ${v.label}`}><span data-testid="trial-chip">{v.chip}</span></Pill>;
}
